import type { TenantStatus } from '@/types/tenant';

const KNOWN: readonly TenantStatus[] = ['active', 'paused', 'offboarded'];

const LABELS: Record<TenantStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  offboarded: 'Offboarded',
};

// Coral/teal tokens only; no raw palette classes or hex.
const STYLES: Record<TenantStatus | 'unknown', string> = {
  active: 'bg-teal-light text-teal-dark border-teal/40',
  paused: 'bg-teal-light text-teal-dark border-teal-dark/40',
  offboarded: 'bg-muted text-muted-foreground border-border',
  unknown: 'bg-warm-bg text-muted-foreground border-border',
};

export function normaliseTenantStatus(
  status: TenantStatus | string | null | undefined,
): TenantStatus | 'unknown' {
  if (!status) return 'unknown';
  // The backend guard compares `lower(trim(...))` (tenant_status_guard.py L38),
  // so ' Active ' means active there and must not read Unknown here.
  const canon = String(status).trim().toLowerCase();
  return (KNOWN as readonly string[]).includes(canon) ? (canon as TenantStatus) : 'unknown';
}

export interface TenantStatusPresentation {
  key: TenantStatus | 'unknown';
  label: string;
  className: string;
}

export function tenantStatusPresentation(
  status: TenantStatus | string | null | undefined,
): TenantStatusPresentation {
  const key = normaliseTenantStatus(status);
  return {
    key,
    label: key === 'unknown' ? 'Unknown' : LABELS[key],
    className: STYLES[key],
  };
}


/**
 * The status value the /clients filter operator compares against for a row.
 *
 * Enum equality on the filter engine can only compare one string; a
 * two-tenant contract where one is Active and one is Offboarded needs to stay
 * visible under the default Active-only rule (Viktor, #2632). This returns
 * the status the row "counts as" for that decision:
 *   - no tenants: 'active' (a contract with no tenant linked is a gap the
 *     /clients page exists to surface; do not hide it),
 *   - any tenant Active: 'active',
 *   - else any tenant Paused: 'paused',
 *   - else any tenant Offboarded: 'offboarded',
 *   - else 'active' (only unknown values left; treat as the DB default).
 *
 * Compares through normaliseTenantStatus so ' Active ' matches the guard
 * (`lower(trim(...))`, tenant_status_guard.py L38).
 */
export function tenantStatusRosterFilterValue(
  tenants: ReadonlyArray<{ status?: string | null }>,
): TenantStatus {
  if (!tenants || tenants.length === 0) return 'active';
  const canon = tenants.map((t) => normaliseTenantStatus(t.status));
  if (canon.some((c) => c === 'active')) return 'active';
  if (canon.some((c) => c === 'paused')) return 'paused';
  if (canon.some((c) => c === 'offboarded')) return 'offboarded';
  return 'active';
}

/**
 * The status the row sorts by: WORST across its tenants, so a contract
 * carrying an offboarded tenant reads as offboarded next to a clean Active
 * neighbour (Viktor, #2632). Ranked so a numeric sort orders
 * offboarded < paused < active; unknown and tenant-less rows read as active.
 */
export function tenantStatusRosterSortValue(
  tenants: ReadonlyArray<{ status?: string | null }>,
): number {
  const rank: Record<TenantStatus | 'unknown', number> = {
    offboarded: 1,
    paused: 2,
    active: 3,
    unknown: 3,
  };
  if (!tenants || tenants.length === 0) return rank.active;
  return Math.min(...tenants.map((t) => rank[normaliseTenantStatus(t.status)]));
}
